import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { z } from 'zod';

import { signIn, type Session } from '@/api/auth';
import { ApiError } from '@/api/http';
import { mutationKeys, queryKeys } from '@/api/queryKeys';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { readerChanged } from '@/app/queryClient';
import { useSession } from '@/app/session';
import { TenantMark } from '@/theme/TenantMark';
import { type } from '@/theme/typography';

/**
 * `DESIGN.md` §6.1 — the only screen an unauthenticated visitor may see.
 *
 * Three things it must not do, all of them undoing a backend decision:
 *
 *  * **say whether the email exists.** The API answers a timing-safe generic failure; repeating
 *    "no such user" here would hand back exactly what that protects.
 *  * **offer a tenant field.** A tenant is never client-supplied — not in a header, a body or a path
 *    (`.claude/rules/tenancy.md`) — and it is not the host's either: login is global and the server
 *    reads `User.tenant` once the credentials check out (ADR 0013). ⚠️ This said *"the tenant comes
 *    from the host, server-side"* until BUG-063, which is the right conclusion from a reason that was
 *    never true of login: constraining an account to its host's tenant was BUG-045's **option A** and
 *    was declined. The host decides which **brand** this page wears — `/branding/`'s fallback for a
 *    caller with no session — and nothing about which accounts may sign in on it.
 *  * **report a throttle as a wrong password.** Login is 5/min per IP (BUG-005); after that the
 *    API refuses before it checks anything, and the screen says so.
 */
const credentialsSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

type Credentials = z.infer<typeof credentialsSchema>;

/**
 * The address to return to after signing in, **or `/` if it is not one this app can own.**
 *
 * ⚠️ `RequireSession` preserves `location.pathname + location.search` and never validated it, and a
 * pathname may legitimately begin with `//`: the catch-all route is a child of the guarded route, so
 * `https://<host>//evil.example` is refused by the guard and arrives here as `from =
 * '//evil.example'`. React Router treats a `//`-prefixed pathname as **absolute** and passes it
 * through untouched. Today both exits use `replace`, so the browser refuses the cross-origin history
 * entry with an uncaught `SecurityError` and strands a signed-in user on this form, where retrying
 * burns the 5/min login throttle (BUG-005); `push` instead falls back to `window.location.assign`,
 * which is a real cross-origin navigation. That is one `replace: true` away from an open redirect on
 * a page showing the genuine host, genuine TLS and the genuine tenant's branding.
 *
 * So: a single leading `/` and nothing else. Backslashes fold in because the URL parser converts
 * `\` to `/` inside a special-scheme path — `/\evil.example` reaches the authority state exactly as
 * `//evil.example` does. Anything that is not a same-origin path at all (`https://…`, a leading
 * control character) fails the same check, because it does not start with `/`.
 *
 * It is validated **where `from` is read**, not where it is written, so both exits below — the
 * imperative navigate and the `<Navigate>` — inherit it and cannot drift apart.
 */
function ownPathOrHome(from: unknown): string {
  if (typeof from !== 'string') return '/';
  const folded = from.replace(/\\/g, '/');
  if (!folded.startsWith('/') || folded.startsWith('//')) return '/';
  return from;
}

export default function SignIn() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const session = useSession();

  /*
   * ⚠️ **The brand is no longer read here.** `TenantMark` reads the same query entry (and `TenantBrand`,
   * above the router, applies §2's properties), so this screen has one fewer thing to keep in step —
   * and the request is still made exactly once for the page, off the same cache key it always was.
   */
  const form = useForm<Credentials>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { email: '', password: '' },
  });

  /**
   * Where `RequireSession` was headed when it found no session (`location.state.from`), or the
   * dashboard. **Both exits below use it**, and that is not a duplication: writing the session into
   * the cache makes `session.isSuccess` true, so the render that follows `onSuccess` reaches the
   * redirect below — and if that one went to `/` unconditionally it would win the race against the
   * imperative navigate and drop the address the user actually asked for.
   *
   * `ownPathOrHome` is why it is safe to hand straight to `navigate`; see its docblock.
   */
  const from = ownPathOrHome((location.state as { from?: unknown } | null)?.from);

  const attempt = useMutation<Session, ApiError, Credentials>({
    // Named so the 401 rule can step over it: a refused login is a wrong password, not an ended
    // session (`queryClient.ts`, `mutationKeys.signIn`).
    mutationKey: mutationKeys.signIn(),
    mutationFn: signIn,
    onSuccess: (signedIn) => {
      /**
       * ⚠️ **Everything cached belongs to whoever was signed in before, so it goes first.**
       *
       * Sign-out empties the cache (`useSignOut`), but sign-out is not the only way an account
       * changes: a session that simply expires lands the user here through `RequireSession`, and
       * the previous user's entries — candidate names in Section 1, their blind-filtered Section 4
       * (ADR 0015) — sit in the client for the default `gcTime` with nobody having pressed
       * anything. On a shared machine the next person's first screen is drawn from them. Sign-in is
       * the one point every account change passes through, so it clears too.
       *
       * ⚠️ **The brand is one of those entries since BUG-045** — `GET /api/v1/branding/` answers the
       * session's tenant now, so the entry in the cache belongs to whoever was here before, and the
       * one observer that does not unmount on the way in (`TenantBrand`, above the router) would go
       * on applying their accent and logo. `readerChanged` empties everything and *refetches* the
       * brand in place rather than dropping it, which is the difference between a refresh that
       * reaches that observer and one that talks to nobody; its docblock has the mechanism, and
       * everything else a change of reader must do (BUG-066) rides along rather than being listed
       * again at each of the four doors.
       *
       * The order matters: clear, then seed the new session, or the seed is thrown away with it.
       */
      readerChanged(queryClient, { emptyTheCache: true });
      queryClient.setQueryData(queryKeys.session(), signedIn);
      void navigate(from, { replace: true });
    },
  });

  if (session.isSuccess) {
    return <Navigate to={from} replace />;
  }

  const failure = attempt.error;
  /*
   * §8's failure states for this screen, in the order they must be told apart. A lost connection
   * reported as "those credentials do not match" sends someone to reset a password that was never
   * the problem, and a throttle reported the same way does it to five people at once.
   *
   * ⚠️ **`unreachable` is the fourth and it is this screen's half of BUG-103.** A 502/503/504 has a
   * response where a dropped connection has none, so it took the `failed` branch and said the
   * credentials did not match — which is the same lie the gate used to tell, arriving at the one
   * moment it does most damage: the reader is here *because* something failed, this is the retry
   * they were invited to make, and a wrong-password sentence sends them off to reset a working
   * password while the backend is down. It says the server instead.
   *
   * ⚠️ **A 401 stays exactly here, above the form, and is not moved beside a field.** §6.1 settled
   * that in Sprint 8 — *"it is not a field that is wrong"*, and saying which of the two was wrong
   * would undo the backend's timing-safe generic refusal. Being the form's own message rather than
   * a redirect is what BUG-103's second case asks for, and it is already what `mutationKeys.signIn`
   * buys in `queryClient.ts`: this mutation's 401 never reaches the session.
   */
  const message = failure?.isOffline
    ? t('offline')
    : failure?.isUnreachable
      ? t('unreachable')
      : failure?.isThrottled
        ? t('throttled')
        : failure
          ? t('failed')
          : null;

  return (
    <main className="flex min-h-screen items-center justify-center bg-paper-bg px-8">
      <div className="w-full max-w-sm">
        {/*
          ⚠️ **The one screen a white-label brand exists for**: it renders before anybody has a
          session, so it is where a tenant's mark tells somebody they are signing in to the right
          place (§2, TASK-102). `TenantMark` falls back to the name — which is what this line was
          until the API had a logo to serve.
        */}
        <TenantMark fallback={t('product')} />
        <h1 className={`${type.pageTitle} mt-2`}>{t('title')}</h1>

        {/*
          Above the form, where §6.1 puts it — "Failure renders one message above the form" — and
          deliberately not beside a field: it is not a field that is wrong. Which of the four
          sentences it carries is decided below, because "wrong password" for a lost connection, an
          unreachable server or a throttle sends someone to reset something that was never the
          problem.
        */}
        {message && (
          <p role="alert" className="mt-4 text-xs text-negative">
            {message}
          </p>
        )}

        <form
          className="mt-6 flex flex-col gap-4"
          onSubmit={form.handleSubmit((credentials) => attempt.mutate(credentials))}
          noValidate
        >
          <Field
            label={t('email')}
            required
            error={form.formState.errors.email ? t('emailInvalid') : undefined}
          >
            {(fieldProps) => (
              <Input
                {...fieldProps}
                {...form.register('email')}
                type="email"
                autoComplete="email"
                autoFocus
              />
            )}
          </Field>

          <Field
            label={t('password')}
            required
            error={form.formState.errors.password ? t('passwordRequired') : undefined}
          >
            {(fieldProps) => (
              <Input
                {...fieldProps}
                {...form.register('password')}
                type="password"
                autoComplete="current-password"
              />
            )}
          </Field>

          <Button type="submit" variant="primary" isLoading={attempt.isPending}>
            {t('submit')}
          </Button>
        </form>
      </div>
    </main>
  );
}
